import { t } from '@lingui/core/macro';
import { ToggleGroup, ToggleGroupItem } from '@/common/components/ui/toggle-group';
import { RANGE_PRESETS, type RangePreset } from '@/features/telemetry/config';
import type { ResolvedRange } from '@/features/telemetry/utils/resolveRange';
import { CustomRangePopover } from '@/features/telemetry/components/range-selector/subcomponents/custom-range-popover/CustomRangePopover';

export interface RangeSelectorProps {
  value: ResolvedRange;
  onChange: (next: { preset: RangePreset } | { from: Date; to: Date }) => void;
  /** Test-only override — see CustomRangePopoverProps.now. */
  now?: Date;
}

const PRESET_ORDER = Object.keys(RANGE_PRESETS) as RangePreset[];

// Built inside the component (not a module-level constant) so it re-evaluates on
// locale change, like every other `t` call in this codebase.
function presetLabels(): Record<RangePreset, string> {
  return {
    '6h': t`6 h`,
    '24h': t`24 h`,
    '7d': t`7 d`,
  };
}

/**
 * Time-range control for the dashboard: a segmented preset group plus a "Custom"
 * button opening a range calendar. Purely controlled — `value` is the currently
 * resolved range, `onChange` reports the user's intent and lets the caller (the
 * route's search params) decide what happens next.
 */
export function RangeSelector({ value, onChange, now }: RangeSelectorProps) {
  // Always a defined string, even with no preset selected (custom range active) —
  // switching a Radix ToggleGroup between a value and `undefined` flips it from
  // controlled to uncontrolled, which React warns about.
  const activePreset = value.preset !== 'custom' ? value.preset : '';
  const labels = presetLabels();

  return (
    <div className="flex items-center gap-1.5">
      <ToggleGroup
        type="single"
        value={activePreset}
        onValueChange={(preset) => {
          if (preset) onChange({ preset: preset as RangePreset });
        }}
        variant="outline"
      >
        {PRESET_ORDER.map((preset) => (
          <ToggleGroupItem key={preset} value={preset} aria-label={labels[preset]}>
            {labels[preset]}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      <CustomRangePopover
        active={value.preset === 'custom'}
        initialFrom={value.from}
        initialTo={value.to}
        onApply={onChange}
        now={now}
      />
    </div>
  );
}
